import { IFutureOrder } from '../../../sp-types/FutureOrderType';
import { DEFAULT_COLOR } from '../../../sp-types/InventoryType';
import { StackedBarChartElement } from '../../../sp-types/dashboardTypes';
import { Material } from '../../../sp-types/materialType';
import { months, MonthYear } from '../../../utils/MonthYear/monthYear';
import { getDate, getMonthYear } from '../../FinancialPositions/components/FinancialPositionsUtils';
import { materialExposureDictionary } from '../FutureOrders';

/* eslint-disable */

export const aggregateFutureOrdersToMaterialsDic = (
  futureOrders: IFutureOrder[],
  selMaterial?: string,
  materialPriceDic?: Record<string, number>,
  isAccumulate: boolean = false,
  isQuant: boolean = false,
): Record<string, materialExposureDictionary> => {
  const materialDictionary: Record<string, materialExposureDictionary> = {};
  const now = getMonthYear(new Date());

  futureOrders
    .filter((order) => !order.dontSum)
    .forEach((order) => {
      const exposures = isAccumulate
        ? order.totalExposure
        : Object.entries(order.monthExposure || {})?.map(([materialId, exposureCalendar]) => ({
            materialId,
            exposureCalendar,
          }));

      exposures?.forEach(({ materialId, exposureCalendar }) => {
        if (!selMaterial || selMaterial === materialId) {
          materialDictionary[materialId] = exposureCalendar
            .filter(({ date }) => (date.month >= now.month && date.year === now.year) || date.year > now.year)
            .reduce(
              (acc, { date, value }) => {
                const updatedValue =
                  materialPriceDic?.[materialId] && !isQuant ? value * materialPriceDic?.[materialId] : value;
                const dateString = date.year.toString() + date.month.toString();
                acc.exposureCalendar[dateString] = (acc.exposureCalendar[dateString] || 0) + updatedValue;
                acc.totalValue += updatedValue;
                return acc;
              },
              materialDictionary[materialId] || {
                exposureCalendar: {},
                totalValue: 0,
                totalWeight: 0,
              },
            );
        }
      });
    });

  return materialDictionary;
};

export const getMonthYearAfter = (date: Date, addMonths: number): MonthYear => {
  const nextDate = getDate(date, addMonths);
  return { month: nextDate.getMonth() + 1, year: nextDate.getFullYear() };
};

export const dictionaryToStackChart = (
  materialMap: Record<string, materialExposureDictionary>,
  colors: Record<string, string>,
  materialsMap: Record<string, Material>,
  year: number,
  pricesMap: Record<string, number>,
  selectedMaterial?: Material | null,
): StackedBarChartElement[] => {
  const now = new Date();
  now.setFullYear(year);

  if (selectedMaterial) {
    const yearMonth = `${year}${now.getMonth() + 1}`;

    const currMonthVal = Object.entries(materialMap)
      .filter(([materialId, _]) => materialId !== selectedMaterial.id)
      .reduce((acc, [_, value]) => acc + (value.exposureCalendar[yearMonth] || 0), 0);
    materialMap['placeholder'] = {
      totalValue: currMonthVal,
      totalWeight: 0,
      price: pricesMap[selectedMaterial.id] || 0,
      exposureCalendar: { [yearMonth]: currMonthVal },
    };
  }

  return Object.entries(materialMap)
    .filter(([materialId]) => !selectedMaterial || ['placeholder', selectedMaterial.id].includes(materialId))
    .map(
      ([materialId, { totalValue, exposureCalendar, price }]): StackedBarChartElement => ({
        materialId,
        fieldName: materialsMap[materialId]?.name,
        color: colors[materialId] || DEFAULT_COLOR,
        valueArray: months
          .map((_, monthIndex) => getMonthYearAfter(now, monthIndex))
          .map(({ month, year }) => ({
            value: exposureCalendar[`${year}${month}`] || 0,
            date: { month, year },
          })),
        sumValue: totalValue,
        totalWeight: totalValue / (pricesMap[materialId] || price || 1),
        unit: materialsMap[materialId]?.unit,
        price: pricesMap[materialId] || 0,
      }),
    );
};

export const getFutureOrderExposedMaterials = (allFutureOrders: IFutureOrder[]): string[] => {
  return Array.from(
    new Set<string>(allFutureOrders.flatMap((order) => (order.monthExposure ? Object.keys(order.monthExposure) : []))),
  );
};
